import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { mocApi } from '../api/client';
import { fmtDate, fmtDateTime, StatusBadge, AlertBadge, CategoryBadge } from '../lib/mocUtils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { canTransition, ROLE_LABELS } from '../lib/permissions';
import { SafetyInDesignTab } from './SafetyInDesign';
import { DeleteMocDialog, isAdminGeneral } from '../components/DeleteMocDialog';
import {
  ArrowLeft, FilePdf, FileImage, Download, Archive, CheckCircle, XCircle,
  CircleNotch, Warning, Lock, UploadSimple, Eye, ShieldCheck, UserPlus, Signature, Trash,
} from '@phosphor-icons/react';

const STAGES = [
  { key: 'soumise', label: 'Évaluation & classification' },
  { key: 'approbation', label: 'Approbation' },
  { key: 'implementation', label: 'Implémentation' },
  { key: 'pret_cloture', label: 'Clôture' },
];
const ORDER = ['brouillon', 'soumise', 'analyse_risques', 'approbation', 'implementation', 'pret_cloture', 'cloturee'];

function actionsFor(status) {
  const a = [];
  if (status === 'brouillon') a.push({ key: 'submit', label: 'Soumettre la MOC' });
  if (status === 'approbation') a.push({ key: 'start_impl', label: "Démarrer l'implémentation" });
  if (status === 'implementation') a.push({ key: 'finish_impl', label: "Terminer l'implémentation" });
  if (!['cloturee', 'annulee'].includes(status)) a.push({ key: 'cancel', label: 'Annuler la MOC', danger: true });
  return a;
}

export default function MocDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get('tab') || 'synthese');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [moc, setMoc] = useState(null);
  const [evalRes, setEvalRes] = useState(null);
  const [pssrData, setPssrData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const load = useCallback(async () => {
    const m = await mocApi.getMoc(id);
    setMoc(m);
    mocApi.evaluateAssistant(id).then(setEvalRes).catch(() => {});
    mocApi.getPssr(id).then(setPssrData).catch(() => setPssrData(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (key) => {
    setBusy(true);
    const res = await mocApi.mocAction(id, key);
    setBusy(false);
    if (res.ok) { toast.success('Action effectuée'); load(); }
    else toast.error(res.error || "L'action a échoué");
  };

  if (!moc) return <div className="h-64 bg-muted animate-pulse rounded-sm" />;
  const currentIdx = ORDER.indexOf(moc.status);
  const acts = actionsFor(moc.status).filter((a) => canTransition(user, moc, a.key));

  return (
    <div className="space-y-5">
      <button data-testid="back-button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground">{moc.id}</span>
            <CategoryBadge category={moc.category} />
            <StatusBadge status={moc.status} />
            <AlertBadge level={moc.alert} />
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight mt-2">{moc.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{moc.site} · {moc.department} · {moc.nature}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {acts.length === 0 && (
            <span className="text-xs text-muted-foreground">Statut : {moc.statusLabel || moc.status} ({ROLE_LABELS[(user?.role || 'pilote').toLowerCase()] || 'Pilote MOC'})</span>
          )}
          {acts.map((a) => (
            <Button key={a.key} data-testid={`action-${a.key}`} disabled={busy}
              variant={a.danger ? 'destructive' : 'default'} size="sm" onClick={() => doAction(a.key)}>
              {busy ? <CircleNotch size={16} className="animate-spin mr-1.5" /> : null}{a.label}
            </Button>
          ))}
          {isAdminGeneral(user) && (
            <Button size="sm" variant="outline" data-testid="moc-delete-button" onClick={() => setDelOpen(true)}
              className="text-destructive border-destructive/40 hover:bg-destructive/10">
              <Trash size={16} weight="bold" className="mr-1.5" />Supprimer
            </Button>
          )}
        </div>
      </div>
      <DeleteMocDialog moc={moc} open={delOpen} onOpenChange={setDelOpen} onDone={() => navigate('/register')} />

      {/* Timeline */}
      <Card className="rounded-sm p-5">
        <h3 className="font-display font-bold mb-5">Progression du workflow</h3>
        <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-0">
          {STAGES.map((s, i) => {
            const idx = ORDER.indexOf(s.key);
            const done = currentIdx > idx || moc.status === 'cloturee';
            const active = ORDER[currentIdx] === s.key || (s.key === 'soumise' && moc.status === 'analyse_risques');
            return (
              <div key={s.key} className="flex-1 flex md:flex-col items-center md:text-center gap-3 md:gap-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 border-2 ${done ? 'bg-emerald-500 border-emerald-500 text-white' : active ? 'bg-primary border-primary text-primary-foreground' : 'bg-muted border-border text-muted-foreground'}`}>
                  {done ? <CheckCircle size={18} weight="fill" /> : i + 1}
                </div>
                <div className="md:mt-2">
                  <div className={`text-xs font-semibold ${active ? 'text-primary' : 'text-foreground'}`}>{s.label}</div>
                  {active && <div className="text-[10px] text-muted-foreground">En cours · {fmtDate(moc.updatedAt)}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 border-b border-border rounded-none w-full justify-start">
          {[['synthese', 'Synthèse'], ['description', 'Description'], ['classification', 'Classification'], ['approbations', 'Approbations'], ['actions', 'Actions'], ['risques', 'Risques'], ['safety', 'Safety In Design'], ['documents', 'Documents'], ['pssr', 'PSSR'], ['cloture', 'Clôture'], ['historique', 'Historique']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2 text-sm">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="synthese" className="pt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Info label="Demandeur" value={moc.requester} />
            <Info label="Site" value={moc.site} />
            <Info label="Service" value={moc.department} />
            <Info label="Nature" value={moc.nature} />
            <Info label="Créée le" value={fmtDate(moc.createdAt)} />
            <Info label="Catégorie" value={moc.category ? `Catégorie ${moc.category}` : 'À confirmer'} />
            <Info label="PSSR requis" value={(evalRes?.pssrObligatoire) ? 'Oui' : (moc.pssrRequired ? 'Oui' : 'Non')} />
            <Info label="Statut" value={moc.statusLabel || moc.status} />
          </div>
        </TabsContent>

        <TabsContent value="description" className="pt-4 space-y-4">
          <Block title="Situation actuelle">{moc.currentSituation}</Block>
          <Block title="Situation future">{moc.futureSituation}</Block>
          <Block title="Type de modification">{(moc.modificationTypes || []).join(', ') || '—'}</Block>
        </TabsContent>

        <TabsContent value="classification" className="pt-4">
          <ClassificationTab id={id} moc={moc} evalRes={evalRes} reload={load} />
        </TabsContent>

        <TabsContent value="approbations" className="pt-4">
          <ApprovalTab id={id} moc={moc} evalRes={evalRes} reload={load} />
        </TabsContent>

        <TabsContent value="actions" className="pt-4">
          <ActionsTab id={id} userEmail={user?.email} />
        </TabsContent>

        <TabsContent value="risques" className="pt-4">
          <RisquesTab id={id} userEmail={user?.email} />
        </TabsContent>

        <TabsContent value="safety" className="pt-4">
          <SafetyInDesignTab mocId={id} userEmail={user?.email} focusNc={sp.get('sid') === 'nc'} />
        </TabsContent>

        <TabsContent value="documents" className="pt-4">
          <DocumentsTab moc={moc} reload={load} />
        </TabsContent>

        <TabsContent value="pssr" className="pt-4">
          <PssrTab id={id} pssrData={pssrData} evalRes={evalRes} reload={load} userEmail={user?.email} />
        </TabsContent>

        <TabsContent value="cloture" className="pt-4">
          <ClosureTab id={id} moc={moc} reload={load} />
        </TabsContent>

        <TabsContent value="historique" className="pt-4">
          <div className="space-y-3">
            {[{ t: 'Dernière mise à jour', d: moc.updatedAt }, { t: 'MOC créée', d: moc.createdAt }].map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="font-medium">{h.t}</span>
                <span className="text-muted-foreground">{fmtDateTime(h.d)}</span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Classification (Pilote / Coordinateur) ----------
function ClassificationTab({ id, moc, evalRes, reload }) {
  const [decision, setDecision] = useState('');
  const [justif, setJustif] = useState('');
  const [busy, setBusy] = useState(false);
  const proposed = evalRes?.categorieProposee;
  const confirmed = !!moc.category;

  useEffect(() => { if (proposed != null && decision === '') setDecision(String(proposed)); }, [proposed]); // eslint-disable-line

  const confirm = async () => {
    setBusy(true);
    const res = await mocApi.confirmClassification(id, Number(decision), justif);
    setBusy(false);
    if (res.ok) { toast.success(`Classification confirmée (catégorie ${res.data?.categorieFinale ?? decision})`); reload(); }
    else toast.error(res.error || 'Échec de la confirmation');
  };

  return (
    <div className="space-y-4">
      {evalRes ? (
        <Card className="rounded-sm p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Proposition du moteur de règles</div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-sm text-white font-black text-2xl ${proposed === 1 ? 'bg-red-600' : proposed === 2 ? 'bg-amber-500' : 'bg-slate-500'}`} data-testid="classif-proposed">Cat.{proposed}</div>
            <div className="text-sm space-y-1">
              <div>PSSR : <b>{evalRes.pssrObligatoire ? 'Obligatoire' : 'Non requise'}</b></div>
              <div>Approbateurs : {(evalRes.approbateursRequis || []).join(', ') || '—'}</div>
              <div>Documents : {(evalRes.documentsRequis || []).join(', ') || '—'}</div>
            </div>
          </div>
        </Card>
      ) : <Empty text="Analyse indisponible." />}

      {confirmed ? (
        <div className="inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm"><CheckCircle size={18} weight="fill" />Classification confirmée : Catégorie {moc.category}</div>
      ) : (
        <Card className="rounded-sm p-4 space-y-3">
          <h3 className="font-display font-bold">Confirmer la classification (Pilote / Coordinateur)</h3>
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Catégorie retenue</label>
              <Select value={decision} onValueChange={setDecision}>
                <SelectTrigger data-testid="classif-decision" className="h-9 mt-1 w-40"><SelectValue placeholder="Catégorie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Catégorie 1</SelectItem>
                  <SelectItem value="2">Catégorie 2</SelectItem>
                  <SelectItem value="3">Catégorie 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {String(proposed) !== decision && (
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Justification (dérogation obligatoire)</label>
              <Textarea data-testid="classif-justif" value={justif} onChange={(e) => setJustif(e.target.value)} className="mt-1 min-h-[70px]" placeholder="Motif de la modification de catégorie" />
            </div>
          )}
          <Button data-testid="classif-confirm" disabled={busy || !decision || (String(proposed) !== decision && justif.trim().length < 4)} onClick={confirm}>
            {busy ? <CircleNotch size={16} className="animate-spin mr-1.5" /> : null}Confirmer la classification
          </Button>
        </Card>
      )}
    </div>
  );
}

// ---------- Approval workflow ----------
function ApprovalTab({ id, moc, evalRes, reload }) {
  const [approver, setApprover] = useState('');
  const [busy, setBusy] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const suggested = evalRes?.approbateursRequis || [];

  const loadA = useCallback(() => {
    mocApi.listApprobateurs(id).then((d) => setApprovers(Array.isArray(d) ? d : []));
    mocApi.listApprobations(id).then((d) => setDecisions(Array.isArray(d) ? d : []));
  }, [id]);
  useEffect(() => { loadA(); }, [loadA]);

  const decisionFor = (email) => decisions.find((x) => (x.Utilisateur_ID || '').toLowerCase() === (email || '').toLowerCase());

  const request = async () => {
    if (!approver.trim()) { toast.error("Renseignez l'e-mail de l'approbateur"); return; }
    setBusy(true);
    const res = await mocApi.requestApproval(id, approver.trim());
    setBusy(false);
    if (res.ok) { toast.success("Demande d'approbation envoyée"); setApprover(''); loadA(); reload(); }
    else toast.error(res.error || 'Échec de la demande');
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-sm p-4">
        <p className="text-sm text-muted-foreground">Statut actuel : <StatusBadge status={moc.status} />. Les décisions se prennent depuis la page <b>Mes validations</b>.</p>
        {suggested.length > 0 && <p className="text-sm mt-2">Rôles approbateurs requis : <b>{suggested.join(', ')}</b></p>}
      </Card>

      {/* Approval progress */}
      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-3">Suivi des approbations</h3>
        {approvers.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune approbation demandée pour l'instant.</p>
        ) : (
          <div className="space-y-2" data-testid="approvers-list">
            {approvers.sort((a, b) => (a.Ordre || 0) - (b.Ordre || 0)).map((ap) => {
              const d = decisionFor(ap.Utilisateur_ID);
              const approved = d && String(d.Decision).toLowerCase().includes('approu');
              const rejected = d && !approved;
              return (
                <div key={ap.ApprobateurRequis_ID} data-testid={`approver-${ap.ApprobateurRequis_ID}`} className="flex items-center justify-between border border-border rounded-sm p-3 gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-medium">{ap.Utilisateur_ID} {ap.Obligatoire && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">· obligatoire</span>}</div>
                    <div className="text-xs text-muted-foreground">Ordre {ap.Ordre}{ap.Role_Requis ? ` · ${ap.Role_Requis}` : ''}{d?.Date_Decision ? ` · ${fmtDate(d.Date_Decision)}` : ''}</div>
                  </div>
                  {approved ? (
                    <span className="text-xs font-semibold text-emerald-600 inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" />Favorable</span>
                  ) : rejected ? (
                    <span className="text-xs font-semibold text-red-600 inline-flex items-center gap-1"><XCircle size={14} weight="fill" />Défavorable</span>
                  ) : (
                    <span className="text-xs font-semibold text-amber-600 inline-flex items-center gap-1"><CircleNotch size={13} />En attente</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="rounded-sm p-4 space-y-3">
        <h3 className="font-display font-bold">Demander une approbation (Pilote MOC)</h3>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">E-mail de l'approbateur</label>
            <Input data-testid="approval-approver" value={approver} onChange={(e) => setApprover(e.target.value)} placeholder="prenom.nom@lafarge.com" className="mt-1 h-9" />
          </div>
          <Button data-testid="approval-request" disabled={busy} onClick={request}>
            {busy ? <CircleNotch size={16} className="animate-spin mr-1.5" /> : null}Demander l'approbation
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------- Corrective actions ----------
const ACTION_ORIGINES = ['PSSR', 'Analyse_Risques', 'Approbation', 'Cloture', 'Autre'];
function ActionsTab({ id, userEmail }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ Description: '', Responsable_ID: '', Echeance: '', Origine: 'PSSR', blocking: false });

  const load = useCallback(() => { mocApi.listActions(id).then((d) => setItems(Array.isArray(d) ? d : [])); }, [id]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.Description.trim() || !form.Echeance) { toast.error('Description et échéance requises'); return; }
    setBusy(true);
    const res = await mocApi.createAction({
      MOC_ID: id, Description: form.Description, Responsable_ID: form.Responsable_ID || userEmail,
      Echeance: form.Echeance, Origine: form.Origine, Obligatoire_Avant_Demarrage: form.blocking,
    });
    setBusy(false);
    if (res.ok) { toast.success('Action créée'); setForm({ Description: '', Responsable_ID: '', Echeance: '', Origine: 'PSSR', blocking: false }); load(); }
    else toast.error(res.error || 'Échec');
  };
  const complete = async (aid) => {
    const evidence = window.prompt('Preuve / commentaire de réalisation :', 'Réalisée');
    if (evidence == null) return;
    const res = await mocApi.closeAction(aid, evidence || 'Réalisée');
    if (res.ok) { toast.success('Action réalisée'); load(); } else toast.error(res.error || 'Échec');
  };

  return (
    <div className="space-y-4" data-testid="actions-panel">
      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-3">Actions correctives</h3>
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} data-testid={`moc-action-${a.id}`} className="flex items-center justify-between border border-border rounded-sm p-3 gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                  {a.title}
                  {a.blocking && <span className="text-[10px] uppercase tracking-wide text-red-600 font-semibold inline-flex items-center gap-1"><Warning size={12} weight="bold" />bloquante</span>}
                </div>
                <div className="text-xs text-muted-foreground">{a.source || '—'} · {a.owner} · échéance {fmtDate(a.due)}</div>
              </div>
              {a.status === 'terminee' ? (
                <span className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1"><CheckCircle size={16} weight="fill" />Réalisée</span>
              ) : (
                <Button size="sm" variant="outline" data-testid={`complete-action-${a.id}`} onClick={() => complete(a.id)}>Marquer réalisée</Button>
              )}
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-muted-foreground">Aucune action pour cette MOC.</p>}
        </div>
      </Card>

      <Card className="rounded-sm p-4 space-y-3">
        <h3 className="font-display font-bold">Nouvelle action</h3>
        <Textarea data-testid="action-desc" placeholder="Description de l'action…" value={form.Description} onChange={(e) => setForm((s) => ({ ...s, Description: e.target.value }))} className="min-h-[70px]" />
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Responsable (e-mail)</label>
            <Input data-testid="action-owner" value={form.Responsable_ID} onChange={(e) => setForm((s) => ({ ...s, Responsable_ID: e.target.value }))} placeholder={userEmail} className="mt-1 h-9" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Échéance</label>
            <Input type="date" data-testid="action-due" value={form.Echeance} onChange={(e) => setForm((s) => ({ ...s, Echeance: e.target.value }))} className="mt-1 h-9" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Origine</label>
            <Select value={form.Origine} onValueChange={(v) => setForm((s) => ({ ...s, Origine: v }))}>
              <SelectTrigger data-testid="action-origine" className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTION_ORIGINES.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="action-blocking" className="accent-primary" checked={form.blocking} onChange={(e) => setForm((s) => ({ ...s, blocking: e.target.checked }))} />
          Bloquante avant démarrage
        </label>
        <Button data-testid="action-create" disabled={busy} onClick={create}>
          {busy ? <CircleNotch size={16} className="animate-spin mr-1.5" /> : null}Ajouter l'action
        </Button>
      </Card>
    </div>
  );
}

// ---------- PSSR ----------
const PSSR_FONCTIONS = ['Pilote_MOC', 'Proprietaire_Systeme', 'Responsable_SST', 'Membre_PSSR', 'Directeur_Site'];
function PssrTab({ id, pssrData, evalRes, reload, userEmail }) {
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [newMember, setNewMember] = useState({ email: '', fonction: 'Membre_PSSR' });
  const pssr = pssrData?.pssr;
  const membres = pssrData?.membres || [];

  useEffect(() => {
    if (pssr?.PSSR_ID) mocApi.getPssrChecklist(pssr.PSSR_ID).then(setChecklist).catch(() => {});
  }, [pssr?.PSSR_ID]);

  const create = async () => {
    setBusy(true); const res = await mocApi.createPssr(id); setBusy(false);
    if (res.ok) { toast.success('PSSR créé'); reload(); } else toast.error(res.error || 'Échec de création du PSSR');
  };
  const addMember = async () => {
    if (!newMember.email.trim()) { toast.error('E-mail requis'); return; }
    const res = await mocApi.addPssrMembre(pssr.PSSR_ID, newMember.email.trim(), newMember.fonction);
    if (res.ok) { toast.success('Membre ajouté'); setNewMember({ email: '', fonction: 'Membre_PSSR' }); reload(); }
    else toast.error(res.error || 'Échec');
  };
  const answer = async (qid, rep) => {
    const res = await mocApi.savePssrResponse(pssr.PSSR_ID, qid, rep);
    if (res.ok) { setChecklist((cl) => cl.map((c) => c.Question_ID === qid ? { ...c, reponseActuelle: rep } : c)); }
    else toast.error(res.error || 'Échec');
  };
  const sign = async (membreId, avis) => {
    const res = await mocApi.signPssrMembre(membreId, avis);
    if (res.ok) { toast.success('Signature enregistrée'); reload(); } else toast.error(res.error || 'Échec');
  };
  const authorize = async (decision) => {
    setBusy(true); const res = await mocApi.authorizeStartup(id, decision); setBusy(false);
    if (res.ok) { toast.success('Décision de mise en route enregistrée'); reload(); } else toast.error(res.error || 'Refusé par le serveur');
  };

  if (!pssr) {
    return (
      <Card className="rounded-sm p-6 text-center space-y-3">
        <ShieldCheck size={36} className="mx-auto text-amber-500" />
        <p className="text-sm text-muted-foreground">{(evalRes?.pssrObligatoire) ? 'Un PSSR est obligatoire pour cette MOC.' : 'Aucun PSSR pour cette MOC.'}</p>
        <Button data-testid="pssr-create" disabled={busy} onClick={create}>{busy ? <CircleNotch size={16} className="animate-spin mr-1.5" /> : null}Créer le PSSR</Button>
      </Card>
    );
  }

  const themes = [...new Set(checklist.map((c) => c.Theme))];
  return (
    <div className="space-y-4" data-testid="pssr-panel">
      <Card className="rounded-sm p-4 flex items-center justify-between flex-wrap gap-2">
        <div><div className="font-mono text-xs text-muted-foreground">{pssr.PSSR_ID}</div><div className="font-semibold text-sm">Statut : {pssr.Statut}</div></div>
        <div className="text-xs text-muted-foreground">Autorisation mise en route : {pssr.Autorisation_Mise_En_Route ? 'Oui' : 'Non'}</div>
      </Card>

      {/* Members */}
      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-3">Équipe PSSR</h3>
        <div className="space-y-2">
          {membres.map((m) => (
            <div key={m.Membre_PSSR_ID} data-testid={`pssr-member-${m.Membre_PSSR_ID}`} className="flex items-center justify-between border border-border rounded-sm p-3 gap-3 flex-wrap">
              <div><div className="text-sm font-medium">{m.Utilisateur_ID}</div><div className="text-xs text-muted-foreground">{m.Fonction}</div></div>
              {m.Avis ? (
                <span className={`text-xs font-semibold inline-flex items-center gap-1 ${m.Avis === 'Oui' ? 'text-emerald-600' : 'text-red-600'}`}><Signature size={14} weight="bold" />Avis : {m.Avis}</span>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`pssr-sign-oui-${m.Membre_PSSR_ID}`} onClick={() => sign(m.Membre_PSSR_ID, 'Oui')}><CheckCircle size={14} className="mr-1" />Favorable</Button>
                  <Button size="sm" variant="outline" data-testid={`pssr-sign-non-${m.Membre_PSSR_ID}`} onClick={() => sign(m.Membre_PSSR_ID, 'Non')}><XCircle size={14} className="mr-1" />Défavorable</Button>
                </div>
              )}
            </div>
          ))}
          {membres.length === 0 && <p className="text-sm text-muted-foreground">Aucun membre. Ajoutez les participants requis.</p>}
        </div>
        <div className="flex gap-2 items-end flex-wrap mt-3 pt-3 border-t border-border">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">E-mail du membre</label>
            <Input data-testid="pssr-member-email" value={newMember.email} onChange={(e) => setNewMember((s) => ({ ...s, email: e.target.value }))} placeholder={userEmail || 'prenom.nom@...'} className="mt-1 h-9" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Fonction</label>
            <Select value={newMember.fonction} onValueChange={(v) => setNewMember((s) => ({ ...s, fonction: v }))}>
              <SelectTrigger data-testid="pssr-member-fonction" className="h-9 mt-1 w-52"><SelectValue /></SelectTrigger>
              <SelectContent>{PSSR_FONCTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="pssr-add-member" variant="outline" onClick={addMember}><UserPlus size={16} className="mr-1.5" />Ajouter</Button>
        </div>
      </Card>

      {/* Checklist */}
      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-1">Checklist de sécurité</h3>
        <p className="text-xs text-muted-foreground mb-4">Toute réponse « Non » peut générer une action (côté serveur).</p>
        {themes.map((t) => (
          <div key={t} className="mb-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">{t}</div>
            <div className="space-y-2">
              {checklist.filter((c) => c.Theme === t).map((c) => (
                <div key={c.Question_ID} data-testid={`pssr-q-${c.Question_ID}`} className="flex items-start justify-between gap-3 border border-border rounded-sm p-3">
                  <span className="text-sm">{c.Libelle_Question}</span>
                  <div className="flex gap-1 shrink-0">
                    {['Oui', 'Non'].map((opt) => (
                      <button key={opt} data-testid={`pssr-${c.Question_ID}-${opt}`} onClick={() => answer(c.Question_ID, opt)}
                        className={`text-xs px-2 py-1 rounded-sm border ${c.reponseActuelle === opt ? (opt === 'Oui' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-red-500 text-white border-red-500') : 'border-border hover:bg-muted'}`}>{opt}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {checklist.length === 0 && <p className="text-sm text-muted-foreground">Checklist vide.</p>}
      </Card>

      {/* Authorization */}
      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-2">Autorisation de mise en route</h3>
        <div className="flex gap-3">
          <Button data-testid="pssr-authorize" disabled={busy} onClick={() => authorize('Autorisee')}><Signature size={16} className="mr-1.5" />Autoriser</Button>
          <Button variant="outline" data-testid="pssr-refuse" disabled={busy} onClick={() => authorize('Refusee')}><Lock size={16} className="mr-1.5" />Refuser</Button>
        </div>
      </Card>
    </div>
  );
}

// ---------- Closure ----------
function ClosureTab({ id, moc, reload }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [justif, setJustif] = useState({});

  const loadCl = useCallback(() => { mocApi.getClotureChecklist(id).then((d) => setItems(Array.isArray(d) ? d : [])); }, [id]);
  useEffect(() => { loadCl(); }, [loadCl]);

  const answer = async (itemId, reponse) => {
    const res = await mocApi.saveClotureReponse(id, itemId, reponse, justif[itemId]);
    if (res.ok) setItems((its) => its.map((i) => i.Item_ID === itemId ? { ...i, reponseActuelle: reponse } : i));
    else toast.error(res.error || 'Échec');
  };
  const ready = items.length > 0 && items.every((i) => i.reponseActuelle === 'Oui' || i.reponseActuelle === 'NA' || (i.reponseActuelle === 'Non' && (justif[i.Item_ID] || '').trim().length > 3));
  const close = async () => {
    setBusy(true); const res = await mocApi.closeMoc(id); setBusy(false);
    if (res.ok) { toast.success('MOC clôturée'); reload(); } else toast.error(res.error || 'Clôture refusée par le serveur');
  };

  if (moc.status === 'cloturee') return <div className="inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm"><CheckCircle size={18} weight="fill" />MOC clôturée</div>;

  return (
    <Card className="rounded-sm p-4">
      <h3 className="font-display font-bold mb-1">Checklist de clôture obligatoire</h3>
      <p className="text-xs text-muted-foreground mb-4">Chaque point doit être « Oui » / « NA », ou « Non » avec justification.</p>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.Item_ID} data-testid={`closure-item-${c.Item_ID}`} className="border border-border rounded-sm p-3">
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm">{c.Ordre}. {c.Libelle}</span>
              <div className="flex gap-1 shrink-0">
                {['Oui', 'Non', 'NA'].map((opt) => (
                  <button key={opt} data-testid={`closure-${c.Item_ID}-${opt}`} onClick={() => answer(c.Item_ID, opt)}
                    className={`text-xs px-2 py-1 rounded-sm border ${c.reponseActuelle === opt ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}>{opt}</button>
                ))}
              </div>
            </div>
            {c.reponseActuelle === 'Non' && (
              <Textarea data-testid={`closure-justif-${c.Item_ID}`} placeholder="Justification…" value={justif[c.Item_ID] || ''}
                onChange={(e) => setJustif((s) => ({ ...s, [c.Item_ID]: e.target.value }))} className="mt-2 text-sm min-h-[56px]" />
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">Checklist indisponible.</p>}
      </div>
      <Button data-testid="close-moc-button" className="mt-4" disabled={!ready || busy} onClick={close}>
        {ready ? null : <Lock size={16} className="mr-1.5" />}Clôturer la MOC
      </Button>
    </Card>
  );
}

const critColor = (c) => c >= 15 ? 'bg-red-600 text-white' : c >= 10 ? 'bg-orange-500 text-white' : c >= 5 ? 'bg-amber-400 text-slate-900' : 'bg-emerald-400 text-slate-900';
const parseRisk = (t) => { const m = /G(\d)[^\d]*P(\d)[^\d]*C(\d+)/i.exec(t || ''); return m ? { g: +m[1], p: +m[2], c: +m[3] } : null; };
function RisquesTab({ id, userEmail }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ desc: '', g: '3', p: '3', mitig: '', owner: '', due: '' });
  const load = useCallback(() => {
    mocApi.listActions(id).then((d) => setItems((Array.isArray(d) ? d : []).filter((a) => /analyse|risque/i.test(a.source || ''))));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const crit = Number(f.g) * Number(f.p);
  const cell = (g, p) => items.filter((a) => { const r = parseRisk(a.title); return r && r.g === g && r.p === p; }).length;

  const add = async () => {
    if (!f.desc.trim() || !f.due) { toast.error('Description et échéance requises'); return; }
    setBusy(true);
    const desc = `[Risque G${f.g}×P${f.p}=C${crit}] ${f.desc}${f.mitig ? ` — Maîtrise: ${f.mitig}` : ''}`;
    const res = await mocApi.createAction({ MOC_ID: id, Description: desc, Responsable_ID: f.owner || userEmail, Echeance: f.due, Origine: 'Analyse_Risques', Obligatoire_Avant_Demarrage: crit >= 10 });
    setBusy(false);
    if (res.ok) { toast.success('Risque enregistré comme action « Analyse de risque »'); setF({ desc: '', g: '3', p: '3', mitig: '', owner: '', due: '' }); load(); }
    else toast.error(res.error || 'Échec');
  };
  const treat = async (aid) => {
    const evidence = window.prompt('Preuve / mesure de maîtrise mise en place :', 'Risque maîtrisé');
    if (evidence == null) return;
    const res = await mocApi.closeAction(aid, evidence || 'Risque maîtrisé');
    if (res.ok) { toast.success('Risque traité'); load(); } else toast.error(res.error || 'Échec');
  };

  return (
    <div className="space-y-4" data-testid="risques-panel">
      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-1">Matrice de criticité (Gravité × Probabilité)</h3>
        <p className="text-xs text-muted-foreground mb-4">Nombre de risques par cellule. Criticité ≥ 10 → action bloquante avant démarrage.</p>
        <div className="overflow-x-auto">
          <table className="border-collapse text-center text-sm">
            <tbody>
              {[5, 4, 3, 2, 1].map((g) => (
                <tr key={g}>
                  <td className="px-2 py-1 text-xs font-semibold text-muted-foreground">G{g}</td>
                  {[1, 2, 3, 4, 5].map((p) => {
                    const c = g * p; const n = cell(g, p);
                    return <td key={p} className={`w-14 h-12 border border-white/40 ${critColor(c)} font-bold`} data-testid={`risk-cell-${g}-${p}`}>{n > 0 ? n : ''}<div className="text-[9px] font-normal opacity-70">{c}</div></td>;
                  })}
                </tr>
              ))}
              <tr><td></td>{[1, 2, 3, 4, 5].map((p) => <td key={p} className="text-xs font-semibold text-muted-foreground pt-1">P{p}</td>)}</tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-3">Registre des risques</h3>
        <div className="space-y-2">
          {items.map((a) => {
            const r = parseRisk(a.title);
            return (
              <div key={a.id} data-testid={`risk-${a.id}`} className="flex items-center justify-between border border-border rounded-sm p-3 gap-3 flex-wrap">
                <div className="min-w-0 flex items-center gap-3">
                  {r && <span className={`inline-flex items-center justify-center w-10 h-10 rounded-sm text-xs font-black shrink-0 ${critColor(r.c)}`}>{r.c}</span>}
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{a.title.replace(/^\[Risque[^\]]*\]\s*/, '')}</div>
                    <div className="text-xs text-muted-foreground">{a.owner} · échéance {fmtDate(a.due)}</div>
                  </div>
                </div>
                {a.status === 'terminee'
                  ? <span className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1"><CheckCircle size={16} weight="fill" />Traité</span>
                  : <Button size="sm" variant="outline" data-testid={`treat-risk-${a.id}`} onClick={() => treat(a.id)}>Marquer traité</Button>}
              </div>
            );
          })}
          {items.length === 0 && <p className="text-sm text-muted-foreground">Aucun risque enregistré.</p>}
        </div>
      </Card>

      <Card className="rounded-sm p-4 space-y-3">
        <h3 className="font-display font-bold">Nouveau risque</h3>
        <Textarea data-testid="risk-desc" placeholder="Description du risque…" value={f.desc} onChange={(e) => setF((s) => ({ ...s, desc: e.target.value }))} className="min-h-[60px]" />
        <Textarea data-testid="risk-mitig" placeholder="Mesure de maîtrise…" value={f.mitig} onChange={(e) => setF((s) => ({ ...s, mitig: e.target.value }))} className="min-h-[50px]" />
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Gravité</label>
            <Select value={f.g} onValueChange={(v) => setF((s) => ({ ...s, g: v }))}><SelectTrigger data-testid="risk-g" className="h-9 mt-1"><SelectValue /></SelectTrigger><SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Probabilité</label>
            <Select value={f.p} onValueChange={(v) => setF((s) => ({ ...s, p: v }))}><SelectTrigger data-testid="risk-p" className="h-9 mt-1"><SelectValue /></SelectTrigger><SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Criticité</label>
            <div className={`mt-1 h-9 rounded-sm flex items-center justify-center font-bold ${critColor(crit)}`} data-testid="risk-crit">{crit}</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Échéance</label>
            <Input type="date" data-testid="risk-due" value={f.due} onChange={(e) => setF((s) => ({ ...s, due: e.target.value }))} className="mt-1 h-9" />
          </div>
        </div>
        <Input data-testid="risk-owner" placeholder={`Responsable (${userEmail || 'e-mail'})`} value={f.owner} onChange={(e) => setF((s) => ({ ...s, owner: e.target.value }))} className="h-9" />
        <Button data-testid="risk-add" disabled={busy} onClick={add}>{busy ? <CircleNotch size={16} className="animate-spin mr-1.5" /> : null}Enregistrer le risque</Button>
      </Card>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="border border-border rounded-sm p-3">
    <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
    <div className="text-sm font-medium mt-1 break-words">{value || '—'}</div>
  </div>
);
const Block = ({ title, children }) => (
  <Card className="rounded-sm p-4"><div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">{title}</div><p className="text-sm leading-relaxed">{children || '—'}</p></Card>
);
const Empty = ({ text }) => <div className="py-10 text-center text-sm text-muted-foreground">{text}</div>;

const DOC_CATEGORIES = ['Analyse des risques', 'Plans', 'Procédure', 'PSSR', 'Autorisation', 'Clôture', 'Autre'];
const ALLOWED_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const MAX_MB = 10;

function DocumentsTab({ moc, reload }) {
  const [category, setCategory] = useState('Analyse des risques');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const docs = moc.documents || [];

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) { toast.error('Type non autorisé (PDF, PNG, JPEG, WEBP)'); e.target.value = ''; return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Fichier trop volumineux (> ${MAX_MB} Mo)`); e.target.value = ''; return; }
    setUploading(true);
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    const b64 = String(dataUrl).split(',')[1];
    const resu = await mocApi.uploadDocument(moc.id, { name: file.name, category, mime: file.type, dataBase64: b64 });
    setUploading(false); e.target.value = '';
    if (resu.ok) { toast.success(`Document ajouté (v${resu.document.version})`); reload(); }
    else toast.error("Échec de l'envoi");
  };

  const openPreview = async (d) => {
    const c = await mocApi.getDocumentContent(moc.id, d.id);
    if (!c.url) { toast.error('Aperçu indisponible'); return; }
    setPreview({ ...d, url: c.url });
  };
  const archive = async (d) => { await mocApi.archiveDocument(moc.id, d.id); toast.success('Document archivé'); reload(); };

  return (
    <div className="space-y-4">
      <Card className="rounded-sm p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Catégorie</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="doc-category" className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <input id="doc-file" type="file" accept=".pdf,image/*" className="hidden" data-testid="doc-file-input" onChange={onFile} />
          <Button asChild disabled={uploading} data-testid="doc-upload-button">
            <label htmlFor="doc-file" className="cursor-pointer">
              {uploading ? <CircleNotch size={16} className="animate-spin mr-1.5" /> : <UploadSimple size={16} className="mr-1.5" />}
              {uploading ? 'Envoi…' : 'Téléverser'}
            </label>
          </Button>
        </div>
      </Card>

      {docs.length === 0 ? <Empty text="Aucun document." /> : (
        <div className="space-y-2">
          {docs.map((d) => (
            <Card key={d.id} data-testid={`doc-${d.id}`} className={`rounded-sm p-3 flex items-center gap-3 ${d.archived ? 'opacity-60' : ''}`}>
              {d.type === 'pdf' ? <FilePdf size={22} className="text-red-500 shrink-0" /> : <FileImage size={22} className="text-sky-500 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{d.name} <span className="text-xs text-muted-foreground">v{d.version}</span></div>
                <div className="text-xs text-muted-foreground">{d.category} · {d.size} · {fmtDate(d.at)}</div>
              </div>
              {d.archived ? (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Archive size={14} />Archivé</span>
              ) : (
                <div className="flex items-center gap-1">
                  <button data-testid={`doc-preview-${d.id}`} onClick={() => openPreview(d)} className="p-1.5 text-muted-foreground hover:text-primary" title="Aperçu"><Eye size={18} /></button>
                  <button data-testid={`doc-download-${d.id}`} onClick={() => openPreview(d)} className="p-1.5 text-muted-foreground hover:text-primary" title="Télécharger"><Download size={18} /></button>
                  <button data-testid={`doc-archive-${d.id}`} onClick={() => archive(d)} className="p-1.5 text-muted-foreground hover:text-destructive" title="Archiver"><Archive size={18} /></button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="text-base">{preview?.name}</DialogTitle></DialogHeader>
          {preview && (
            <div className="w-full">
              {preview.type === 'pdf'
                ? <iframe title="preview" src={preview.url} className="w-full h-[70vh] rounded-sm border border-border" />
                : <img alt={preview.name} src={preview.url} className="max-h-[70vh] w-auto mx-auto rounded-sm" />}
              <div className="mt-3 flex justify-end">
                <a href={preview.url} download={preview.name} data-testid="doc-download-link">
                  <Button size="sm" variant="outline"><Download size={16} className="mr-1.5" />Télécharger</Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

